import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  Banner,
  Icon,
  PageHeader,
  RecordLayout,
  Section,
  WS_COLOR,
  WS_RADIUS,
  WorkspaceThemeProvider,
  icons,
} from '@platform/ui-kit/workspace';
import { api, type AirPlatform, type AirPlatformAssessment } from '../../../api';
import { heatCell } from './heatInk';

/**
 * Strategic Intelligence → AI Platforms Catalog → one platform, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `AiPlatformDetail.tsx`, which stays
 * the default until the owner approves this one (CLAUDE.md 1.2). Layout 3, Record workspace,
 * without a facts rail: the original shows no facts beside the profile, and none is invented.
 *
 * WHAT IS UNCHANGED: the API calls (get, ask, refresh), every field and its order (positioning and
 * summary, How it runs / deploys / Learning curve, At a glance, Good for / Less good for,
 * Coverage by function or industry, Reference, Ask about this platform), the fallbacks (pros /
 * cons), the rating and coverage FILLS (`favColor`, `covColor` from the source, hue for hue),
 * the legend, and every label and message.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header, one scroll
 * region), the back link as a header button, flat sections instead of stacked cards, the ink on
 * each heat cell chosen to pass contrast (the fills are kept, except the two that no ink can
 * read on, which are darkened a step: heatInk.ts), the coverage toggle as pressed
 * buttons, and names and live regions for assistive technology.
 */

/** The source's covColor(): thresholds and fills unchanged. */
function covFill(score: unknown): string {
  const v = Number(score);
  if (!v) return '#E0E0E0';
  if (v >= 5) return '#1A9850';
  if (v >= 4) return '#91CF60';
  if (v >= 3) return '#FEE08B';
  if (v >= 2) return '#FC8D59';
  return '#D73027';
}

/** The source's favColor(): thresholds and fills unchanged. */
function favFill(rating: unknown): string {
  const r = Number(rating);
  if (r >= 5) return '#2e7d32';
  if (r >= 4) return '#66bb6a';
  if (r >= 3) return '#fdd835';
  if (r >= 2) return '#fb8c00';
  return '#e53935';
}

interface CoverageCell {
  id: string;
  label: string;
  score: number;
  rationale?: string;
}

const COV_LEGEND: [number, string][] = [
  [5, 'Best-in-class'],
  [4, 'Strong'],
  [3, 'Adequate'],
  [2, 'Weak'],
  [1, 'Minimal'],
];

const CONSTRUCT_FIELDS: ReadonlyArray<[string, string]> = [
  ['execution', 'How it runs'],
  ['deployment', 'How it deploys'],
  ['learning', 'Learning curve'],
];

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export function AiPlatformDetailWorkspace({ platformId }: { platformId: string }) {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen platformId={platformId} />
    </WorkspaceThemeProvider>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="tonal" onClick={onClick} startIcon={<Icon icon={icons.ArrowLeft} size={16} />}>
      Back to AI Platforms
    </Button>
  );
}

function Screen({ platformId }: { platformId: string }) {
  const navigate = useNavigate();
  // Stay in the workspace version: the switch lives in the query string.
  const { search } = useLocation();
  const [platform, setPlatform] = useState<AirPlatform | null>(null);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [covView, setCovView] = useState<'function' | 'industry'>('function');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  useEffect(() => {
    if (!platformId) return;
    let active = true;
    setLoading(true);
    api.aiReadiness
      .getPlatform(platformId)
      .then((d) => {
        if (active) setPlatform(d.platform);
      })
      .catch((e) => {
        if (active) setError(e?.message || 'Could not load the platform.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [platformId]);

  const backToCatalog = () =>
    navigate(`/ai_readiness/strategic-intelligence/ai-platforms-catalog${search}`);

  if (loading)
    return (
      <Typography role="status" sx={{ color: WS_COLOR.textMuted }}>
        Loading…
      </Typography>
    );

  if (error || !platform) {
    return (
      <Box sx={{ display: 'grid', gap: 3, justifyItems: 'start' }}>
        <Banner tone="error">{error || 'Platform not found.'}</Banner>
        <BackButton onClick={backToCatalog} />
      </Box>
    );
  }

  const profile = platform.profile ?? {};
  const construct = (profile.construct ?? {}) as Record<string, string>;
  const assessment: AirPlatformAssessment[] = Array.isArray(profile.assessment)
    ? profile.assessment
    : [];
  const cov = (profile.coverage ?? {}) as { function?: CoverageCell[]; industry?: CoverageCell[] };
  const fnCov: CoverageCell[] = Array.isArray(cov.function) ? cov.function : [];
  const indCov: CoverageCell[] = Array.isArray(cov.industry) ? cov.industry : [];
  const covCells = covView === 'industry' ? indCov : fnCov;

  const reference: [string, string][] = (
    [
      ['cost_model', 'Cost model'],
      ['security', 'Security & compliance'],
      ['erp_connectivity', 'ERP connectivity'],
      ['differentiators', 'Differentiators'],
    ] as [string, string][]
  ).filter(([k]) => profile[k]);

  const ask = () => {
    const q = question.trim();
    if (!q) return;
    setAsking(true);
    setAskError(null);
    setAnswer('Searching the web…');
    api.aiReadiness
      .askPlatform(platformId, q)
      .then((d) => setAnswer(d.answer || '(no answer)'))
      .catch((e: Error) => {
        setAnswer(null);
        setAskError(e.message);
      })
      .finally(() => setAsking(false));
  };

  const pull = () => {
    setPulling(true);
    setAskError(null);
    api.aiReadiness
      .refreshPlatform(platformId)
      // Re-read rather than patch: refresh MERGES over the stored profile server side.
      .then(() => api.aiReadiness.getPlatform(platformId))
      .then((d) => setPlatform(d.platform))
      .catch((e: Error) => setAskError(e.message))
      .finally(() => setPulling(false));
  };

  const goodFor = asStringList(profile.good_for).length
    ? asStringList(profile.good_for)
    : asStringList(profile.pros);
  const lessGoodFor = asStringList(profile.less_good_for).length
    ? asStringList(profile.less_good_for)
    : asStringList(profile.cons);
  const summary = (profile.summary as string) || (profile.overview as string) || '';
  const positioning = (profile.positioning as string) || '';
  const hasConstruct = CONSTRUCT_FIELDS.some(([k]) => construct[k]);

  return (
    <RecordLayout
      header={
        <PageHeader
          title={platform.name}
          subtitle={platform.category || undefined}
          actions={<BackButton onClick={backToCatalog} />}
        />
      }
    >
      {positioning || summary || hasConstruct ? (
        <Section title="Overview">
          {positioning ? (
            <Typography variant="h3" component="p" sx={{ fontWeight: 700 }}>
              {positioning}
            </Typography>
          ) : null}
          {summary ? <Typography sx={{ mt: positioning ? 1 : 0 }}>{summary}</Typography> : null}
          {hasConstruct ? (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gap: 4,
                m: 0,
                mt: positioning || summary ? 4 : 0,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              }}
            >
              {CONSTRUCT_FIELDS.filter(([k]) => construct[k]).map(([k, label]) => (
                <Box key={k}>
                  <Typography component="dt" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0, mt: 1 }}>
                    {construct[k]}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Section>
      ) : null}

      <Section title="At a glance">
        {assessment.length ? (
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gap: 3,
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
          >
            {assessment.map((a, i) => {
              const cell = heatCell(favFill(a.rating));
              return (
                <Box
                  component="li"
                  key={`${a.area ?? 'area'}-${i}`}
                  sx={{
                    background: cell.bg,
                    color: cell.fg,
                    borderRadius: `${WS_RADIUS.field}px`,
                    p: 3,
                  }}
                >
                  <Box sx={{ fontSize: 12 }}>{a.area}</Box>
                  <Box sx={{ fontWeight: 700, fontSize: 18, m: '2px 0 6px' }}>{a.label || ''}</Box>
                  <Box sx={{ fontSize: 12, lineHeight: 1.3 }}>{a.note || ''}</Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Banner tone="warning">No at-a-glance profile generated yet.</Banner>
        )}
      </Section>

      <Section title="Good for and less good for">
        <Box
          sx={{
            display: 'grid',
            gap: 5,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          <Box>
            <Typography variant="h3" component="h3">
              Good for
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 5 }}>
              {goodFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </Box>
            {profile.best_fit ? (
              <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                {String(profile.best_fit)}
              </Typography>
            ) : null}
          </Box>
          <Box>
            <Typography variant="h3" component="h3">
              Less good for
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 5 }}>
              {lessGoodFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </Box>
          </Box>
        </Box>
      </Section>

      {fnCov.length || indCov.length ? (
        <Section
          title="Coverage"
          actions={
            <Box role="group" aria-label="View by" sx={{ display: 'flex', gap: 2 }}>
              <Button
                size="small"
                variant={covView === 'function' ? 'contained' : 'tonal'}
                aria-pressed={covView === 'function'}
                onClick={() => setCovView('function')}
              >
                Function
              </Button>
              <Button
                size="small"
                variant={covView === 'industry' ? 'contained' : 'tonal'}
                aria-pressed={covView === 'industry'}
                onClick={() => setCovView('industry')}
              >
                Industry
              </Button>
            </Box>
          }
          description="Fit 1–5 · hover a cell for the rationale"
        >
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 2,
            }}
          >
            {covCells.map((cell) => {
              const tint = heatCell(covFill(cell.score));
              return (
                <Box
                  component="li"
                  key={cell.id}
                  title={cell.rationale || ''}
                  sx={{
                    background: tint.bg,
                    color: tint.fg,
                    borderRadius: `${WS_RADIUS.field}px`,
                    p: 3,
                  }}
                >
                  <Box sx={{ fontSize: 12, lineHeight: 1.2, minHeight: 30 }}>{cell.label}</Box>
                  <Box sx={{ fontSize: 22, fontWeight: 700, mt: 0.5 }}>{cell.score || '—'}</Box>
                </Box>
              );
            })}
          </Box>
          <Box
            component="ul"
            aria-label="Scale"
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 3,
              p: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {COV_LEGEND.map(([score, label]) => (
              <Box
                component="li"
                key={score}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '3px',
                    background: heatCell(covFill(score)).bg,
                  }}
                />
                <Typography variant="caption">
                  {score} · {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>
      ) : null}

      {reference.length ? (
        <Section title="Reference">
          {reference.map(([key, label]) => (
            <Typography key={key} sx={{ my: 1 }}>
              <strong>{label}:</strong> {String(profile[key])}
            </Typography>
          ))}
        </Section>
      ) : null}

      <Section
        title="Ask about this platform"
        description="Ask a specific question — the AI assistant will search the web for current details."
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder="e.g. What's the latest pricing? Does it run air-gapped? Recent product changes?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          inputProps={{ 'aria-label': 'Question about this platform' }}
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 3 }}>
          <Button variant="contained" onClick={ask} disabled={asking || !question.trim()}>
            {asking ? 'Searching…' : 'Ask'}
          </Button>
          {/* The source pairs Ask with a full re-profile: the same web search can refresh the record. */}
          <Button variant="tonal" onClick={pull} disabled={pulling}>
            {pulling ? 'Pulling…' : 'Pull latest data'}
          </Button>
        </Box>
        {askError ? (
          <Box sx={{ mt: 3 }}>
            <Banner tone="error">{askError}</Banner>
          </Box>
        ) : null}
        <Box aria-live="polite">
          {answer ? (
            <Box
              sx={{
                mt: 3,
                p: 4,
                border: `1px solid ${WS_COLOR.border}`,
                borderRadius: `${WS_RADIUS.field}px`,
                whiteSpace: 'pre-wrap',
              }}
            >
              {answer}
            </Box>
          ) : null}
        </Box>
      </Section>
    </RecordLayout>
  );
}
